<template>
	<div class="cand-dashboard-maincontainer overflow-hidden">
		<!-- Mobile Header (Sticky) -->
		<div class="mobile-streak-header">
			<img
				class="mr-3 pointer"
				src="@/assets/mobile/assprog/mobileBackArrow.svg"
				alt="back"
				@click="goBack"
			/>
			<span class="header-title">Your Streak & Activity</span>
		</div>

		<!-- Desktop Header -->
		<div class="cand-header d-flex" id="hideinmobile">
			<div class="mr-3 pointer">
				<img
					@click="goBack"
					src="../../assets/img/homepage/back-arrow-blue.svg"
				/>
			</div>
			Your Streak & Activity
		</div>

		<div
			class="dashboard d-flex gap-3 align-items-start w-100 mt-20px"
			:class="{ 'dashboard-collapsed': isScrolled }"
		>
			<div class="left-section-streak w-70" @scroll="handleScroll">
				<!-- Streaks + Assessments -->
				<div class="streak-cards-wrapper">
					<div>
						<StreaksCard ref="streaksCard" />
					</div>
					<div class="mt-20px">
						<AssessmentCourses />
					</div>
				</div>
			</div>
			<div
				class="right-section-streak w-30"
				:class="{ 'streak-collapsed': isScrolled }"
			>
				<!-- Streak card -->
				<StreakCard
					:showViewButton="false"
					:hideStats="true"
					@day-selected="onWeekDaySelected"
				/>
			</div>
		</div>
	</div>
</template>
<script>
import AssessmentCourses from "@/components/MyProfile/AssessmentCourses.vue";
import StreaksCard from "@/components/MyProfile/StreaksCard.vue";
import StreakCard from "@/components/MyProfile/StreakCard.vue";
export default {
	name: "StreakPage",
	components: {
		AssessmentCourses,
		StreaksCard,
		StreakCard,
	},
	beforeRouteEnter(to, from, next) {
		// Capture the true previous route before entering /streaks.
		// This is the only place we can reliably read `from` before the component mounts.
		next((vm) => {
			// Only store non-streaks routes so we never "go back" to /streaks itself
			if (from && from.path && !from.path.startsWith("/streaks")) {
				vm._fromRoute = from.fullPath;
			}
		});
	},
	data() {
		return {
			isScrolled: false,
			_fromRoute: null,
			_scrollLockUntil: 0,
			_scrollLockValue: null,
			_preClickScrollTop: 0,
		};
	},
	mounted() {
		window.addEventListener("scroll", this.handleScroll, true);
	},
	beforeDestroy() {
		window.removeEventListener("scroll", this.handleScroll, true);
	},
	methods: {
		onWeekDaySelected(dateStr) {
			this.$refs.streaksCard &&
				this.$refs.streaksCard.onCellClick(dateStr, true);
		},
		lockScrollState(ms = 800) {
			if (ms === 0) {
				// On desktop, restore scroll position while lock is still active
				// to ignore programmatic scroll events from the reflow.
				// On mobile, skip this — the DOM reflow during data loading changes
				// the page height, making the saved position unreliable. The CSS
				// state (isScrolled) is preserved by _scrollLockValue below, so the
				// user can scroll freely from wherever the browser landed.
				if (window.innerWidth > 768) {
					this.restoreScrollPosition();
				}

				if (this._scrollLockValue !== null) {
					this.isScrolled = this._scrollLockValue;
				} else {
					this.isScrolled = this.getActualScroll() > 15;
				}

				// On mobile, if the streak is hidden (isScrolled=true), ensure
				// leftSection.scrollTop is just above the 15px threshold so the user
				// can scroll back up past it to re-show the weekly streak.
				// Without this, DOM reflow may reset scroll to 0, leaving the
				// user unable to generate scroll-up events (already at top).
				if (window.innerWidth <= 768 && this.isScrolled) {
					const leftSection = document.querySelector(".left-section-streak");
					if (leftSection && typeof leftSection.scrollTop === "number") {
						leftSection.scrollTop = 20;
					}
				}

				this._scrollLockValue = null;
				this._scrollLockUntil = 0;
			} else {
				const lockActive = Date.now() < this._scrollLockUntil;
				if (!lockActive || this._scrollLockValue === null) {
					// Freeze isScrolled at its current value.
					this._scrollLockValue = this.isScrolled;
					// Save current scroll position
					this._preClickScrollTop = this.getActualScroll();
				}
				// Extend the lock duration
				this._scrollLockUntil = Date.now() + ms;
			}
		},
		getActualScroll() {
			const winScroll =
				window.scrollY || document.documentElement.scrollTop || 0;
			let targetScroll = 0;
			const leftSection = document.querySelector(".left-section-streak");
			if (leftSection && typeof leftSection.scrollTop === "number") {
				targetScroll = leftSection.scrollTop;
			}
			return Math.max(winScroll, targetScroll);
		},
		restoreScrollPosition() {
			const scrollVal = this._preClickScrollTop;
			if (scrollVal > 0) {
				window.scrollTo(0, scrollVal);
				if (document.documentElement)
					document.documentElement.scrollTop = scrollVal;
				if (document.body) document.body.scrollTop = scrollVal;

				const leftSection = document.querySelector(".left-section-streak");
				if (leftSection && typeof leftSection.scrollTop === "number") {
					leftSection.scrollTop = scrollVal;
				}
			}
		},
		goBack() {
			// On mobile, always go straight to home — no history tracking needed.
			if (window.innerWidth <= 768) {
				this.$router.push({ name: "Homepage" });
				return;
			}
			// Desktop: go back to wherever we came from, or fall back to browser history.
			if (this._fromRoute) {
				this.$router.push(this._fromRoute);
			} else {
				this.$router.back();
			}
		},
		handleScroll(e) {
			// Ignore scroll events from the heatmap's horizontal strip or SVG —
			// those are left/right swipes or re-render reflows, never vertical page scroll.
			if (e && e.target) {
				const t = e.target;
				if (t.id === "mobile-heatmap-scroll") return;
				if (t.closest && t.closest("#mobile-heatmap-scroll")) return;
				if (t.id === "heatmap2") return;
				if (t.closest && t.closest("#heatmap2")) return;
			}

			const lockActive = Date.now() < this._scrollLockUntil;
			const targetId = e?.target?.id || e?.target?.className || "unknown";
			const winScroll =
				window.scrollY || document.documentElement.scrollTop || 0;
			let targetScroll = 0;
			if (e && e.target && typeof e.target.scrollTop === "number") {
				targetScroll = e.target.scrollTop;
			}
			const activeScroll = Math.max(winScroll, targetScroll);

			console.log("[handleScroll]", {
				targetId,
				tagName: e?.target?.tagName,
				winScroll,
				targetScroll,
				activeScroll,
				lockActive,
				lockValue: this._scrollLockValue,
				currentIsScrolled: this.isScrolled,
				wouldSet: activeScroll > 15,
			});

			// While a cell-click re-render is in progress, freeze isScrolled
			// at the value it had when lockScrollState was called.
			if (lockActive) {
				if (this._scrollLockValue !== null) {
					this.isScrolled = this._scrollLockValue;
				}
				return;
			}

			// Clear lock value once expired
			if (this._scrollLockValue !== null) {
				this._scrollLockValue = null;
			}

			this.isScrolled = activeScroll > 15;
		},
	},
};
</script>
<style>
.mt-20px {
	margin-top: 20px;
}
.w-30 {
	width: 30%;
}

.left-section-streak {
	/* Adjust the offset to account for the new desktop header */
	height: calc(100vh - 180px);
	overflow-y: auto;
	/* Gap between the content and the scrollbar */
	padding-right: 7px;
}

/* Custom scrollbar styling to make it look clean */
.left-section-streak::-webkit-scrollbar {
	width: 5px;
}
.left-section-streak::-webkit-scrollbar-thumb {
	background: #20639b4d;
	border-radius: 4px;
}
.left-section-streak::-webkit-scrollbar-track {
	background: transparent;
}

.mobile-streak-header {
	display: none;
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
	.streak-cards-wrapper {
		padding: 15px;
	}
	.cand-dashboard-maincontainer {
		padding-top: calc(0px + env(safe-area-inset-top)) !important;
		padding: 0;
		position: unset;
		height: 100vh;
		height: 100dvh;
		overflow: hidden !important;
		display: flex;
		flex-direction: column;
		overscroll-behavior: none;
	}
	.mobile-streak-header {
		display: flex;
		align-items: center;
		width: 100%;
		flex-shrink: 0;
		height: 50px; /* Reduced height */
		position: relative; /* Restored from sticky */
		background: #f6f9fb;
		z-index: 1020;
		padding: 10px 15px; /* Reduced top/bottom padding */
		margin-bottom: 0px;
	}
	.header-title {
		font-family: "Lato", sans-serif;
		font-weight: 600;
		font-size: 18px;
		color: #20639b;
	}
	.dashboard.d-flex {
		flex-direction: column;
		margin-top: 0 !important;
		flex-grow: 1; /* Automatically snaps neatly to exactly the remaining screen */
		height: 0;
		overflow: hidden !important;
	}
	.left-section-streak,
	.right-section-streak {
		width: 100% !important;
		padding-right: 0;
	}

	.left-section-streak {
		order: 2;
		margin-top: 5px; /* Tiny boundary visual separator */
		flex-grow: 1;
		height: 0;
		overflow-y: auto !important; /* Enable scrolling */
		padding-bottom: 50px !important; /* Reasonable padding */
		position: relative;
		z-index: 1 !important;
	}

	.right-section-streak {
		order: 1;
		flex-shrink: 0;
		/* Replaced sticky with strict static parent bounds: it mathematically CANNOT overlap bottom content under any circumstance */
		position: relative;
		z-index: 1010 !important;
		width: 100% !important;
	}
}

/* Override the StreakCard bottom-sheet logic from the child component */
@media (max-width: 1028px) {
	.right-section-streak .streak-card {
		position: relative !important;
		bottom: auto !important;
		border-radius: 0 0 12px 12px !important;
		padding: 16px !important;
		max-height: 500px; /* Allows full expansion */
		transition: max-height 0.3s ease;
		overflow: hidden;
	}
	.right-section-streak .st-grey-dragger {
		display: none !important;
	}
	.right-section-streak.streak-collapsed .streak-card {
		max-height: 88px !important; /* Only hero section visible */
	}
}
</style>
